/**
 * Mock OpenFire REST API v1.12.0
 * Simulates OpenFire REST endpoints for admin UI
 */
import { RESTAdapter } from '../adapters/rest-adapter';
// ============================================================================
// Mock OpenFire REST API
// ============================================================================
export class MockOpenFireAPI {
    constructor(storage, domain = 'wargame.local', conferenceService = 'conference.wargame.local') {
        this.adapter = new RESTAdapter(storage, domain, conferenceService);
    }
    // ==========================================================================
    // Users
    // ==========================================================================
    async getUsers(params) {
        let users = await this.adapter.getAllUsers();
        // Apply search filter
        if (params?.search) {
            const searchLower = params.search.toLowerCase();
            users = users.filter((u) => u.username.toLowerCase().includes(searchLower));
        }
        // Apply pagination
        const startIndex = params?.startIndex || 0;
        const count = params?.count || users.length;
        return users.slice(startIndex, startIndex + count);
    }
    async getUser(username) {
        return await this.adapter.getUser(username);
    }
    async createUser(user) {
        const existing = await this.adapter.getUser(user.username);
        if (existing) {
            throw new Error(`User ${user.username} already exists`);
        }
        return await this.adapter.createUser(user);
    }
    async updateUser(username, updates) {
        const updated = await this.adapter.updateUser(username, updates);
        if (!updated) {
            throw new Error(`User ${username} not found`);
        }
        return updated;
    }
    async deleteUser(username) {
        // Delete is idempotent - no error if user doesn't exist
        await this.adapter.deleteUser(username);
    }
    // ==========================================================================
    // Groups (Forces)
    // ==========================================================================
    async getGroups() {
        return await this.adapter.getAllGroups();
    }
    async getGroup(name) {
        return await this.adapter.getGroup(name);
    }
    async createGroup(group) {
        const existing = await this.adapter.getGroup(group.name);
        if (existing) {
            throw new Error(`Group ${group.name} already exists`);
        }
        return await this.adapter.createGroup(group);
    }
    async updateGroup(name, updates) {
        const updated = await this.adapter.updateGroup(name, updates);
        if (!updated) {
            throw new Error(`Group ${name} not found`);
        }
        return updated;
    }
    async deleteGroup(name) {
        const existing = await this.adapter.getGroup(name);
        if (!existing) {
            throw new Error(`Group ${name} not found`);
        }
        await this.adapter.deleteGroup(name);
    }
    async addGroupMember(groupName, username) {
        const group = await this.adapter.getGroup(groupName);
        if (!group) {
            throw new Error(`Group ${groupName} not found`);
        }
        await this.adapter.addGroupMember(groupName, username);
    }
    async removeGroupMember(groupName, username) {
        const group = await this.adapter.getGroup(groupName);
        if (!group) {
            throw new Error(`Group ${groupName} not found`);
        }
        await this.adapter.removeGroupMember(groupName, username);
    }
    // ==========================================================================
    // Rooms (MUC)
    // ==========================================================================
    async getRooms() {
        return await this.adapter.getAllRooms();
    }
    async getRoom(roomName) {
        return await this.adapter.getRoom(roomName);
    }
    async createRoom(room) {
        const existing = await this.adapter.getRoom(room.roomName);
        if (existing) {
            throw new Error(`Room ${room.roomName} already exists`);
        }
        return await this.adapter.createRoom(room);
    }
    async updateRoom(roomName, updates) {
        const updated = await this.adapter.updateRoom(roomName, updates);
        if (!updated) {
            throw new Error(`Room ${roomName} not found`);
        }
        return updated;
    }
    async deleteRoom(roomName) {
        const existing = await this.adapter.getRoom(roomName);
        if (!existing) {
            throw new Error(`Room ${roomName} not found`);
        }
        await this.adapter.deleteRoom(roomName);
    }
    async addRoomMember(roomName, jid, _role) {
        const room = await this.adapter.getRoom(roomName);
        if (!room) {
            throw new Error(`Room ${roomName} not found`);
        }
        // Extract username from JID and add to members
        const username = jid.split('@')[0];
        if (!username)
            return;
        const members = room.members || [];
        if (!members.includes(jid)) {
            members.push(jid);
            await this.adapter.updateRoom(roomName, { members });
        }
    }
    async removeRoomMember(roomName, jid, _role) {
        const room = await this.adapter.getRoom(roomName);
        if (!room) {
            throw new Error(`Room ${roomName} not found`);
        }
        const members = (room.members || []).filter((m) => m !== jid);
        await this.adapter.updateRoom(roomName, { members });
    }
}
//# sourceMappingURL=openfire-api.js.map